export type MatchingRequest = {
  city?: string | null;
  proposed_rate?: number | null;
  care_days?: number[] | null;
  care_at_pro_home?: boolean | null;
  care_at_parent_home?: boolean | null;
  care_outdoor?: boolean | null;
};

export type MatchingPro = {
  city?: string | null;
  hourly_rate?: number | null;
  available_days?: number[] | null;
  is_available?: boolean | null;
  identity_verified?: boolean | null;
  care_at_pro_home?: boolean | null;
  care_at_parent_home?: boolean | null;
  care_outdoor?: boolean | null;
  latitude?: number | null;
  longitude?: number | null;
  service_radius?: number | null;
};

export type MatchingResult = {
  score: number;
  label: "Excellent" | "Très bon" | "Bon" | "À vérifier";
  reasons: string[];
  distanceKm: number | null;
};

const norm = (value?: string | null) => (value || "").trim().toLocaleLowerCase("fr-FR");

export function distanceKm(
  aLat?: number | null,
  aLng?: number | null,
  bLat?: number | null,
  bLng?: number | null,
) {
  if ([aLat, aLng, bLat, bLng].some((v) => v == null || !Number.isFinite(v))) return null;
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad((bLat as number) - (aLat as number));
  const dLng = rad((bLng as number) - (aLng as number));
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat as number)) * Math.cos(rad(bLat as number)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function scoreCareMatch(
  request: MatchingRequest & { latitude?: number | null; longitude?: number | null },
  pro: MatchingPro,
): MatchingResult {
  let score = 25;
  const reasons: string[] = [];

  if (norm(request.city) && norm(request.city) === norm(pro.city)) {
    score += 25;
    reasons.push("Même ville");
  }

  const d = distanceKm(request.latitude, request.longitude, pro.latitude, pro.longitude);
  if (d != null) {
    const radius = Number(pro.service_radius || 25);
    if (d <= radius) {
      score += d <= 5 ? 20 : d <= 15 ? 15 : 10;
      reasons.push(`${Math.round(d)} km`);
    } else {
      score -= 10;
    }
  }

  if (pro.is_available) {
    score += 10;
    reasons.push("Disponible");
  }

  const requestedDays = request.care_days || [];
  const availableDays = pro.available_days || [];
  if (requestedDays.length && availableDays.length) {
    const covered = requestedDays.filter((day) => availableDays.includes(day)).length;
    const ratio = covered / requestedDays.length;
    score += Math.round(ratio * 15);
    if (ratio === 1) reasons.push("Jours compatibles");
  }

  if (request.proposed_rate != null && pro.hourly_rate != null) {
    const gap = Math.abs(Number(request.proposed_rate) - Number(pro.hourly_rate));
    if (gap <= 1) {
      score += 10;
      reasons.push("Tarif compatible");
    } else if (gap <= 3) score += 5;
  }

  const locationChecks = [
    [request.care_at_pro_home, pro.care_at_pro_home, "Garde chez le pro"],
    [request.care_at_parent_home, pro.care_at_parent_home, "Garde à domicile"],
    [request.care_outdoor, pro.care_outdoor, "Sorties / extérieur"],
  ] as const;
  locationChecks.forEach(([wanted, offered, reason]) => {
    if (wanted && offered) {
      score += 5;
      reasons.push(reason);
    }
  });

  if (pro.identity_verified) {
    score += 5;
    reasons.push("Profil vérifié");
  }

  score = Math.max(0, Math.min(100, score));
  const label = score >= 85 ? "Excellent" : score >= 70 ? "Très bon" : score >= 55 ? "Bon" : "À vérifier";
  return { score, label, reasons: reasons.slice(0, 4), distanceKm: d };
}
